import { Component } from '../../decorators/component.decorator';
import { Child, Children } from '../../decorators/child.decorator';
import { Labels } from '../../global/labels';
import { keyCodes, TabIndex } from '../../global/keyboard';
import { Digest } from '../../global/digest';
import { UIDateElement } from '../datetime/ui-dateelement';
import { addLeadingZero } from '../../global/helpers';
import { setInnerText, updateElement } from '../../global/ui-helpers';
import { UIMonthPicker } from '../datetime/ui-monthpicker';
import type { ElementConfig } from '../ui-element.type';
import type { NullOr, UndefinedOr } from '../../global/utils.type';
import type { TranslationLabels } from '../../types/labels.type';
import type {
    UICalendarConfig,
    UICalendarMonth,
    UICalendarPeriod,
    UICalendarRenderSource,
    UICalendarRenderSources,
    UICalendarWeekday,
} from './ui-calendar.type';
import '../datetime/ui-monthpicker';
import styles from './ui-calendar.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIDateElement}
 * @alias UICalendar
 * @element ui-calendar
 * @classdesc Represents a class for <code>ui-calendar</code> element.
 * Renders calendar element, mostly used in combination with <code>ui-datepicker</code>.
 * @fires event:change
 * @property {string} min {@attr min} Minimum date value of the calendar.
 * @property {string} max {@attr max} Maximum date value of the calendar.
 * @property {string} value {@attr value} Current date value of the calendar,
 * referenced of the control/input element.
 * @property {Locale} [locale="en"] {@attr locale} Localization rules in 4 character-format (e.g., "lt-LT"). Applies to ui-monthpicker.
 * @property {HTMLParagraphElement} message Shortcut to accessibility assist message.
 * @property {UIMonthPicker} monthPicker
 * @property {HTMLTableCellElement} month
 * @property {HTMLInputElement} control
 * @property {HTMLDivElement} calendar
 * @property {HTMLButtonElement} prevMonthButton
 * @property {HTMLButtonElement} nextMonthButton
 * @property {NodeList<HTMLButtonElement>} dayButtons
 * @property {HTMLDivElement} header
 * @property {NodeList<HTMLTableCellElement>} activeDayButtons
 * @property {HTMLTableCellElement} today
 * @example
 * <ui-calendar><input type="date" value="01.04.2019" /></ui-calendar>
 */
@Component({ tag: 'ui-calendar', styles })
export class UICalendar extends UIDateElement {
    @Child('.ui-calendar__input') control: HTMLInputElement = null!;
    @Child('.ui-calendar__month') month!: HTMLTableCellElement;
    @Child('ui-monthpicker') monthPicker!: UIMonthPicker;
    @Child('.ui-calendar__calendar') calendar!: HTMLDivElement;
    @Child('.ui-calendar__navbutton.-prev') prevMonthButton!: HTMLButtonElement;
    @Child('.ui-calendar__navbutton.-next') nextMonthButton!: HTMLButtonElement;
    @Child('.ui-calendar__header') header!: HTMLElement;
    @Child('.ui-calendar__message') message!: HTMLParagraphElement;
    @Child('.ui-calendar__daybutton.-today') today?: HTMLTableCellElement;
    @Children('.ui-calendar__daybutton')
    dayButtons!: NodeListOf<HTMLTableCellElement>;
    @Children('.ui-calendar__daybutton:not(.-disabled)')
    activeDayButtons!: NodeListOf<HTMLTableCellElement>;

    private _config?: UICalendarConfig;
    private focusedDate: NullOr<string> = null;
    private focusTimer?: ReturnType<typeof setTimeout>;
    private observer?: MutationObserver;
    private boundCalendarKeydown?: (e: Event) => void;
    private boundDayFocusin?: (e: Event) => void;
    private boundDayClick?: (e: Event) => void;

    /**
     * Define labels what could be localised
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-calendar', {
            weekDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            weekDaysLong: [
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
                'Sunday',
            ],
            prevMonth: 'Previous month',
            nextMonth: 'Next month',
            arrowKeys: 'Arrow keys can navigate dates',
        });
    }

    static resolveMonthKey(index: number): UICalendarMonth {
        return (
            [
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec',
            ] as const
        )[index % 12];
    }

    static resolveWeekdaysKey(index: number): UICalendarWeekday {
        return (['Sun', 'Mon', 'Tue', 'Wen', 'Thu', 'Fr', 'Sat'] as const)[
            index % 7
        ];
    }

    /**
     * When call update calendar, these are list of possible sources where from calendar is updated.
     * @type {{
     *  CALENDAR_CHANGED: string,
     *  RENDERED: string,
     *  DATE_SELECTED: string,
     *  CONTROL_CHANGED: string,
     *  MONTH_CHANGED: string,
     *  PERIOD_CHANGED: string,
     *  INPUT_CHANGED: string
     * }}
     */
    static readonly RENDER_SOURCES: UICalendarRenderSources = {
        RENDERED: 'rendered',
        MONTH_CHANGED: 'month-changed',
        CONTROL_CHANGED: 'control-changed',
        PERIOD_CHANGED: 'period-changed',
        CALENDAR_CHANGED: 'calendar-changed',
        DATE_SELECTED: 'date-selected',
        INPUT_CHANGED: 'input-changed',
    } as const;

    get value(): NullOr<string> {
        if (!this.isValidDate(this.control.value)) {
            return null;
        }
        return this.reformatDateString(this.control.value);
    }

    set value(value: NullOr<string>) {
        if (!this.isValidDate(value) || this.control.value === value) {
            return;
        }
        this.control.value = value!;
    }

    /**
     * @type {number}
     */
    static readonly ASSIST_MSG_TIMEOUT = 200;

    /**
     * @param {ICalendarConfig} config
     */
    setConfig(config: UICalendarConfig): void {
        this._config = config;
    }

    /**
     * @returns {ICalendarConfig}
     */
    getConfig(): UndefinedOr<UICalendarConfig> {
        return this._config;
    }

    /**
     * Sets year and month to the monthpicker.
     * @param {number} year
     * @param {number} month
     */
    setActiveMonth(year: number, month: number): void {
        this.monthPicker.value = [year, month].join('-');

        // Move the keyboard-focus indicator to the corresponding day in the new
        // month (clamped so e.g. Jan 31 → Feb 28) without committing a new value.
        const referenceDate = this.focusedDate || this.value || '';
        const { day } = this.parseDateValue(referenceDate, this.min, this.max);
        this.focusedDate = this.clampDate(year, month, day);

        this.updateCalendar(
            year,
            month,
            UICalendar.RENDER_SOURCES.MONTH_CHANGED
        );
        this.monthPicker.recalculateMonthWidth();
    }

    /**
     * Gets clamped date string for given year, month and day values.
     * @param {number} year
     * @param {number} month
     * @param {number} day
     * @private
     * @returns {string} formatted date string
     */
    private clampDate(year: number, month: number, day: number): string {
        // 0th day of next month is the last day of current month
        const lastDay = new Date(year, month, 0).getDate();
        const clampedDay = Math.min(day, lastDay);
        // date string that is either equal to the selected/current date, or the last day of the month.
        // for example:
        // if selected date is 31.01.2019 and user moves to the month of february (which has 28 days),
        // then the 28.02.2019 will appear active. if the user continues to march (which has 31 days),
        // then 31.03.2019 will appear active.
        return this.formatDateString(clampedDay, month, year);
    }

    /**
     * Update a calendar appearance according to given year and month values.
     * @param {number} year
     * @param {number} month
     * @param {string} source
     * @returns {UICalendar}
     */
    updateCalendar(
        year: number,
        month: number,
        source: UICalendarRenderSource
    ): InstanceType<typeof UICalendar> {
        this.updateMonthGrid(year, month);
        this.updateMonthAndHeaderTexts();

        if (source === UICalendar.RENDER_SOURCES.RENDERED) {
            this.insertElements([
                {
                    tagName: 'p',
                    classList: {
                        'disclaimer': true,
                        'ui-calendar__message': true,
                        '-hidden': true,
                    },
                    attributes: {
                        'aria-live': 'polite',
                    },
                    children: [UICalendar.labels.arrowKeys],
                },
            ]);
        } else {
            this.toggleMonthNavigationButtons(new Date(year, month, 0));
        }

        return this;
    }

    /**
     * Updates the calendar dates for a given year and month.
     * This method calculates the days to display in the calendar grid, including
     * days from the previous and next months, and updates the DOM elements accordingly.
     * @param {number} year
     * @param {number} month
     */
    updateMonthGrid(year: number, month: number): void {
        const todayDateString = this.timestampToDateString(Date.now());
        const value = this.value ?? '';
        const periods: Array<UICalendarPeriod> =
            this.getConfig()?.periods?.filter(
                (p) =>
                    Array.isArray(p.months) &&
                    p.months.includes(UICalendar.resolveMonthKey(month - 1))
            ) || [];

        let startDay = new Date(year, month - 1, 1).getDay();
        if (startDay === 0) {
            startDay = 7;
        }

        // focusedDate tracks keyboard navigation position; fall back to the
        // committed value so the selected cell is always reachable by Tab.
        let focusDateString = value;
        let focusDate = this.parseDateString(value);
        if (this.focusedDate) {
            const focused = this.parseDateString(this.focusedDate);
            if (focused.year === year && focused.month === month) {
                focusDateString = this.focusedDate;
                focusDate = focused;
            }
        }
        const isFocusedInCurrentMonth =
            focusDate.year === year && focusDate.month === month;
        let tabbableCellFound = false;

        this.querySelectorAll<HTMLTableCellElement>('td[data-index]').forEach(
            (node) => {
                const index = Number(node.dataset.index);
                const nodeDay = new Date(year, month - 1, index + 2 - startDay);
                const nodeDate = nodeDay.getDate();
                const nodeMonth = nodeDay.getMonth();
                const monthLabels = UIMonthPicker.labels
                    .months as TranslationLabels.Labels;
                const monthLabel = monthLabels[nodeMonth] ?? '';
                const isOtherMonth = nodeMonth !== month - 1;

                let dateString: NullOr<string> = null;
                let isSelected = false;
                let isToday = false;
                let isTabbable = false;
                let isDisabled = true;

                if (!isOtherMonth) {
                    dateString = this.formatDateString(
                        Number(addLeadingZero(nodeDate)),
                        Number(addLeadingZero(month)),
                        year
                    );

                    isSelected = dateString === value;
                    isToday = dateString === todayDateString;
                    isDisabled =
                        !this.isDateInRange(nodeDay) ||
                        this.isDateInactive(nodeDay, periods);

                    // isFocused: the keyboard-navigated cell (or selected cell
                    // when no navigation has occurred yet).
                    const isFocused = dateString === focusDateString;
                    isTabbable =
                        isFocused ||
                        (!tabbableCellFound &&
                            !isFocusedInCurrentMonth &&
                            !isDisabled);

                    if (isTabbable) {
                        tabbableCellFound = true;
                    }
                }

                updateElement(node, {
                    classList: {
                        '-disabled': isDisabled,
                        '-selected': isSelected,
                        '-today': isToday,
                    },
                    attributes: {
                        'role': 'gridcell',
                        'tabindex': isTabbable
                            ? TabIndex.Active
                            : TabIndex.Inactive,
                        'data-date': dateString,
                        'aria-label': `${(UICalendar.labels.weekDaysLong as TranslationLabels.Labels)[index % 7]} ${nodeDate} ${monthLabel} ${year}`,
                        'aria-current': isToday ? 'date' : null,
                        'aria-hidden': isTabbable ? null : 'true',
                        'aria-disabled': isDisabled ? 'true' : null,
                        'aria-selected':
                            !isOtherMonth && isSelected ? 'true' : 'false',
                    },
                });

                setInnerText(node, String(nodeDate));
            }
        );
    }

    /**
     * Determines if a given date is inactive based on specified periods.
     * @param {Date} date - The date to check for inactivity.
     * @param {Array<object>} periods - An array of period objects.
     * @private
     * @returns {boolean}
     */
    private isDateInactive(
        date: Date,
        periods: Array<UICalendarPeriod>
    ): boolean {
        return periods?.some((p) => {
            if (p.type !== 'inactive') {
                return false;
            }

            if (Array.isArray(p.dates) && p.dates.includes(date.getDate())) {
                return true;
            }

            return (
                Array.isArray(p.weekdays) &&
                p.weekdays.includes(
                    UICalendar.resolveWeekdaysKey(date.getDay())
                )
            );
        });
    }

    /**
     * Updates the displayed month and header text based on selected month and year.
     */
    updateMonthAndHeaderTexts(): void {
        const monthSelect =
            this.monthPicker.monthSelect?.selectedOptions[0]?.text;
        const yearSelect =
            this.monthPicker.yearSelect?.selectedOptions[0]?.text;

        if (!monthSelect || !yearSelect) {
            return;
        }

        const headerText = `${monthSelect} ${yearSelect}`;

        if (monthSelect !== this.month.innerText) {
            setInnerText(this.month, monthSelect);
        }
        if (headerText !== this.header.innerText) {
            setInnerText(this.header, headerText);
        }
    }

    /**
     * Sets value to the controls.
     * @param {string} value
     * @param {string} [source]
     * @returns {UICalendar}
     */
    updateControlValue(
        value: string,
        source?: UICalendarRenderSource
    ): InstanceType<typeof UICalendar> {
        const validValue = this.isValidDate(value);
        const data = this.parseDateValue(value, this.min, this.max);
        const isNewValue = this.value !== data.value;
        if (validValue && isNewValue) {
            this.value = data.value;
        }
        if (isNewValue || source === 'date-selected') {
            this.dispatchCustomEvent('change', { source: source });
        }
        const monthPicker = this.monthPicker;
        const monthValue = [data.year, data.month].join('-');
        if (monthPicker && monthPicker.value !== monthValue) {
            monthPicker.value = monthValue;
        }
        this.updateCalendar(
            data.year,
            data.month,
            UICalendar.RENDER_SOURCES.CONTROL_CHANGED
        );
        return this;
    }

    /**
     * Fires callback when period is changed.
     * @private
     */
    private onChangePeriod(): void {
        const picker = this.monthPicker;
        if (this.min) {
            const minParts = this.parseDateString(this.min);
            const newMinValue = this.formatMonthString(
                minParts.month,
                minParts.year
            );
            if (picker.min !== newMinValue) {
                picker.min = newMinValue;
            }
        }
        if (this.max) {
            const maxParts = this.parseDateString(this.max);
            const newMaxValue = this.formatMonthString(
                maxParts.month,
                maxParts.year
            );
            if (picker.max !== newMaxValue) {
                picker.max = newMaxValue;
            }
        }
        this.updateControlValue(
            this.value || '',
            UICalendar.RENDER_SOURCES.PERIOD_CHANGED
        );
    }

    /**
     * Fires callback when the host element's value is updated.
     * @param {Event} e
     * @private
     */
    private handleValueUpdate(e: Event): void {
        if (e.target !== this) {
            return;
        }
        this.updateControlValue(
            this.value || '',
            UICalendar.RENDER_SOURCES.CALENDAR_CHANGED
        );
    }

    /**
     * Fires callback when user clicks on the next-month button.
     * @private
     */
    private handleNextMonth(): void {
        const maxDateValue = this.getMaxDate();
        const picker = this.monthPicker;
        const data = this.parseMonthValue(
            picker.value!,
            picker.min,
            picker.max
        );
        const date = new Date();
        date.setFullYear(data.year, data.month, 1);
        date.setHours(0, 0, 0, 0);
        this.toggleMonthNavigationButtons(date);
        if (this.nextMonthButton.disabled) {
            this.monthPicker.monthSelect?.focus();
        }
        if (maxDateValue && maxDateValue < date) {
            return;
        }
        this.setActiveMonth(date.getFullYear(), date.getMonth() + 1);
    }

    /**
     * Fires callback when user clicks on the previous-month button.
     * @private
     */
    private handlePrevMonth(): void {
        const minDateValue = this.getMinDate();
        const picker = this.monthPicker;
        const data = this.parseMonthValue(
            picker.value!,
            picker.min,
            picker.max
        );
        const date = new Date();
        date.setFullYear(data.year, data.month - 1, 0);
        this.toggleMonthNavigationButtons(date);
        if (this.prevMonthButton.disabled) {
            picker.monthSelect?.focus();
        }
        if (minDateValue && minDateValue > date) {
            return;
        }
        this.setActiveMonth(date.getFullYear(), date.getMonth() + 1);
    }

    /**
     * Resolves disability of next/prev buttons.
     * @param {Date} date
     * @private
     */
    private toggleMonthNavigationButtons(date: Date): void {
        const d = new Date(date); // we don't mutate original date.
        const min = this.getMinDate();
        const max = this.getMaxDate();
        if (min && min instanceof Date) {
            this.prevMonthButton.disabled =
                min.getMonth() >= d.getMonth() &&
                min.getFullYear() >= d.getFullYear();
        }
        if (max && max instanceof Date) {
            this.nextMonthButton.disabled =
                max.getMonth() <= d.getMonth() &&
                max.getFullYear() <= d.getFullYear();
        }
    }

    /**
     * Fires callback when user changes active month using the monthpicker.
     * @private
     */
    private handleMonthUpdate(): void {
        const data = this.parseMonthValue(
            this.monthPicker.value!,
            this.monthPicker.min,
            this.monthPicker.max
        );
        this.setActiveMonth(data.year, data.month);
    }

    /**
     * Fires callback when user clicks on a date.
     * @param {Event} e
     * @private
     */
    private handleDaySelect(e: Event): void {
        if (!(e.target instanceof HTMLElement)) {
            return;
        }
        if (e.target.classList.contains('-disabled')) {
            return;
        }
        // Clear navigation-only focus so the newly selected cell becomes tabbable.
        this.focusedDate = null;
        this.updateControlValue(
            e.target.dataset.date || '',
            UICalendar.RENDER_SOURCES.DATE_SELECTED
        );
    }

    /**
     * Handles enter and space keys.
     * @param {KeyboardEvent} e
     * @private
     */
    private handleKeyboardSelect(e: KeyboardEvent): void {
        if ([keyCodes.ENTER, keyCodes.SPACE].includes(e.keyCode)) {
            this.handleDaySelect(e);
        }
    }

    /**
     * Fires callback when user focus on a date grid.
     * @param {Event} e
     * @private
     */
    private handleDayFocus(e: Event): void {
        this.setMessageDelayed(UICalendar.labels.arrowKeys as string);
    }

    /**
     * Fires callback when the input control gets a change event.
     * @param {Event} e
     * @private
     */
    handleInputChange(e: Event): void {
        if (!(e.target instanceof HTMLInputElement)) {
            return;
        }
        this.updateControlValue(
            e.target.value,
            UICalendar.RENDER_SOURCES.INPUT_CHANGED
        );
    }

    /**
     * @inheritDoc
     */
    handleControlMutations(mutations: Array<MutationRecord>): void {
        for (const mutation of mutations) {
            if (mutation.type !== 'attributes') {
                continue;
            }
            if (
                !['value', 'min', 'max'].includes(mutation.attributeName || '')
            ) {
                continue;
            }
            if (!(mutation.target instanceof Element)) {
                continue;
            }
            if (
                mutation.oldValue !==
                mutation.target.getAttribute(mutation.attributeName || '')
            ) {
                this.onChangePeriod();
                return;
            }
        }
    }

    /**
     * Handles enter and space keys.
     * Tab navigation inside calendar is not possible due to datepicker logic,
     * should use arrow keys instead.
     * @param {KeyboardEvent} event
     * @private
     */
    handleKeyDown(event: KeyboardEvent): void {
        // Prevent default (like scroll with arrow keys)
        // ENTER and SPACE are handled by handleKeyboardSelect
        if (![keyCodes.ENTER, keyCodes.SPACE].includes(event.keyCode)) {
            event.preventDefault();
        }
    }

    /**
     * Handles arrow keys.
     * @param {KeyboardEvent} event
     * @private
     */
    handleArrowKeys(event: KeyboardEvent): void {
        event.preventDefault();
        const activeElement = document.activeElement;
        if (!(activeElement instanceof HTMLElement)) {
            return;
        }
        const activeDate = activeElement.dataset.date;
        if (!activeDate) {
            return;
        }

        const d = new Date(this.dateStringToTimestamp(activeDate));
        if (activeElement.classList.contains('ui-calendar__daybutton')) {
            switch (event.keyCode) {
                case keyCodes.LEFT:
                    this.moveToPreviousDay(d);
                    break;
                case keyCodes.RIGHT:
                    this.moveToNextDay(d);
                    break;
                case keyCodes.UP:
                    this.moveToPreviousWeek(d);
                    break;
                case keyCodes.DOWN:
                    this.moveToNextWeek(d);
                    break;
                case keyCodes.PAGEUP:
                    this.moveToNextMonth(d);
                    break;
                case keyCodes.PAGEDOWN:
                    this.moveToPreviousMonth(d);
                    break;
                /* istanbul ignore next */
                default:
                    break;
            }
        }
    }

    /**
     * Sets the messages for accessibility with slight delay to make them be read
     * by screen-readers.
     * @param {string} msg
     */
    setMessageDelayed(msg: string): void {
        if (this.message.innerText !== msg) {
            setTimeout(
                () => setInnerText(this.message, msg),
                UICalendar.ASSIST_MSG_TIMEOUT
            );
        }
    }

    /**
     * Moves keyboard focus to the given date without committing a new value.
     * Only arrow-key navigation calls this; actual selection happens in
     * handleDaySelect / handleKeyboardSelect.
     * @param {Date} d
     * @private
     */
    setFocusDay(d: Date): void {
        const min = this.getMinDate() || new Date(1970, 0, 1);
        const max = this.getMaxDate() || new Date(9999, 0, 1);

        const tmpDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        if (tmpDate < min || tmpDate > max) {
            return;
        }

        const value = this.timestampToDateString(d.getTime());
        this.focusedDate = value;

        const year = d.getFullYear();
        const month = d.getMonth() + 1;
        const monthValue = [year, month].join('-');

        if (this.monthPicker.value !== monthValue) {
            this.monthPicker.value = monthValue;
        }
        this.updateCalendar(
            year,
            month,
            UICalendar.RENDER_SOURCES.MONTH_CHANGED
        );

        // Cancel any previously queued focus jump so rapid key-holding does not
        // queue up stale timers that later fire and visibly jump focus backwards.
        if (this.focusTimer !== undefined) {
            clearTimeout(this.focusTimer);
        }
        // Defer focus until after the current grid repaint is complete.
        this.focusTimer = setTimeout(() => {
            delete this.focusTimer;
            const btn = this.querySelector<HTMLElement>(
                '[data-date="' + value + '"]'
            );
            btn?.focus();
        });
    }

    /**
     * Move date to previous day.
     * @param {Date} d
     * @private
     */
    moveToPreviousDay(d: Date): void {
        d.setDate(d.getDate() - 1);
        this.setFocusDay(d);
    }

    /**
     * Move date to next day.
     * @param {Date} d
     * @private
     */
    moveToNextDay(d: Date): void {
        d.setDate(d.getDate() + 1);
        this.setFocusDay(d);
    }

    /**
     * Move date to previous week.
     * @param {Date} d
     * @private
     */
    moveToPreviousWeek(d: Date): void {
        d.setDate(d.getDate() - 7);
        this.setFocusDay(d);
    }

    /**
     * Move date to next week.
     * @param {Date} d
     * @private
     */
    moveToNextWeek(d: Date): void {
        d.setDate(d.getDate() + 7);
        this.setFocusDay(d);
    }

    /**
     * Move date to next month.
     * @param {Date} d
     * @private
     */
    moveToNextMonth(d: Date): void {
        // create a new date at the first day of next month to avoid month overflow
        const target = new Date(d.getFullYear(), d.getMonth() + 1, 1);
        // clamp date to avoid overflow of days to next month (ie move 2 months instead of 1)
        const clampedDate = this.clampDate(
            target.getFullYear(),
            target.getMonth() + 1,
            d.getDate()
        );
        const clampedTimestamp = this.dateStringToTimestamp(clampedDate);
        this.setFocusDay(new Date(clampedTimestamp));
    }

    /**
     * Move date to previous month.
     * @param {Date} d
     * @private
     */
    moveToPreviousMonth(d: Date): void {
        // create a new date at the first day of next month to avoid month overflow
        const target = new Date(d.getFullYear(), d.getMonth() - 1, 1);
        // clamp date to avoid overflow of days to previous month (ie move 2 months instead of 1)
        const clampedDate = this.clampDate(
            target.getFullYear(),
            target.getMonth() + 1,
            d.getDate()
        );
        const clampedTimestamp = this.dateStringToTimestamp(clampedDate);
        this.setFocusDay(new Date(clampedTimestamp));
    }

    /**
     * @param {MouseEvent} event
     * @private
     */
    handleClick(event: MouseEvent): void {
        if (document.activeElement?.localName === 'body') {
            setInnerText(this.message, '');
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const uuid = Digest.randomId();
        const control = this.queryChildren('input')[0] as HTMLInputElement;
        control.classList.add('ui-calendar__input');
        const value = control.getAttribute('value');
        const valueParts = value ? this.parseDateString(value) : null;
        let minParts;
        let maxParts;

        if (control) {
            (['value', 'min', 'max'] as const).forEach((a) => {
                if (control.hasAttribute(a)) {
                    const attrValue = this[a];
                    const formattedValue =
                        typeof attrValue === 'string'
                            ? this.reformatDateString(attrValue)
                            : '';
                    control.setAttribute(a, formattedValue);
                }
            });
        }

        const monthPickerAttributes: {
            value: NullOr<string>;
            min?: string;
            max?: string;
        } = {
            value: valueParts
                ? this.formatMonthString(valueParts.month, valueParts.year)
                : null,
        };
        if (this.min) {
            minParts = this.parseDateString(this.min);
            monthPickerAttributes.min = this.formatMonthString(
                minParts.month,
                minParts.year
            );
        }
        if (this.max) {
            maxParts = this.parseDateString(this.max);
            monthPickerAttributes.max = this.formatMonthString(
                maxParts.month,
                maxParts.year
            );
        }

        this.setAttribute('aria-labelledby', uuid);
        const weekDays = UICalendar.labels.weekDays as Array<string>;
        const tableChildren = [
            {
                tagName: 'thead',
                attributes: {
                    'aria-hidden': 'true',
                },
                children: [
                    {
                        tagName: 'tr',
                        children: weekDays.map((label) => {
                            return {
                                tagName: 'th',
                                attributes: {
                                    scope: 'col',
                                },
                                children: [label],
                            };
                        }),
                    },
                ],
            },
            {
                tagName: 'tbody',
                children: [
                    {
                        tagName: 'tr',
                        attributes: {
                            'aria-hidden': 'true',
                        },
                        children: [
                            {
                                tagName: 'td',
                                attributes: {
                                    colspan: 7,
                                },
                                classList: {
                                    'ui-calendar__month': true,
                                },
                            },
                        ],
                    },
                    ...[0, 1, 2, 3, 4, 5].map((week): ElementConfig => {
                        return {
                            tagName: 'tr',
                            attributes: { role: 'row' },
                            children: [0, 1, 2, 3, 4, 5, 6].map(
                                (day): ElementConfig => {
                                    return {
                                        tagName: 'td',
                                        attributes: {
                                            'data-index': day + week * 7,
                                            'tabindex': TabIndex.Inactive,
                                        },
                                        classList: {
                                            'ui-calendar__daybutton': true,
                                        },
                                    };
                                }
                            ),
                        };
                    }),
                ],
            },
        ] satisfies Array<ElementConfig>;

        this.insertElements([
            {
                tagName: 'h2',
                attributes: {
                    'aria-live': 'polite',
                    'id': uuid,
                },
                classList: {
                    'ui-calendar__header': true,
                    '-visually-hidden': true,
                },
            },
            {
                tagName: 'input',
                element: control,
                classList: {
                    'ui-calendar__input': true,
                },
                attributes: {
                    value: value,
                    type: 'hidden',
                    min: this.min,
                    max: this.max,
                    pattern: this.dateRegExp.toString(),
                },
            },
            {
                tagName: 'div',
                classList: {
                    'ui-calendar__header': true,
                },
                children: [
                    {
                        tagName: 'ui-button',
                        attributes: {
                            'layout': 'tertiary',
                            'glyph': 'left',
                            'aria-label': UICalendar.labels.prevMonth,
                            'disabled':
                                minParts &&
                                valueParts &&
                                minParts.month >= valueParts.month &&
                                minParts.year >= valueParts.year
                                    ? true
                                    : null,
                        },
                        classList: {
                            'ui-calendar__navbutton': true,
                            '-prev': true,
                        },
                    },
                    {
                        tagName: 'ui-monthpicker',
                        attributes: {
                            locale: this.getAttribute('locale'),
                        },
                        children: [
                            {
                                tagName: 'input',
                                attributes: monthPickerAttributes,
                            },
                        ],
                    },
                    {
                        tagName: 'ui-button',
                        attributes: {
                            'layout': 'tertiary',
                            'glyph': 'right',
                            'aria-label': UICalendar.labels.nextMonth,
                            'disabled':
                                maxParts &&
                                valueParts &&
                                maxParts.month <= valueParts.month &&
                                maxParts.year <= valueParts.year
                                    ? true
                                    : null,
                        },
                        classList: {
                            'ui-calendar__navbutton': true,
                            '-next': true,
                        },
                    },
                ],
            },
            {
                tagName: 'div',
                classList: {
                    'ui-calendar__calendar': true,
                },
                children: [
                    {
                        tagName: 'table',
                        attributes: {
                            role: 'grid',
                        },
                        children: tableChildren,
                    },
                ],
            },
        ]);

        const validValue = this.isValidDate(value);
        const data = this.parseDateValue(value || '', this.min, this.max);
        if (validValue && this.value !== data.value) {
            this.value = data.value;
        }
        this.updateCalendar(
            data.year,
            data.month,
            UICalendar.RENDER_SOURCES.RENDERED
        );
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.updateElement({
            events: {
                change: (event?: Event | CustomEvent) =>
                    this.handleValueUpdate(event as Event),
                keydown: (event?: Event | CustomEvent) =>
                    this.handleKeyDown(event as KeyboardEvent),
                click: (event?: Event | CustomEvent) =>
                    this.handleClick(event as MouseEvent),
            },
        });
        updateElement(this.control, {
            events: {
                change: (event?: Event | CustomEvent) =>
                    this.handleInputChange(event as Event),
            },
        });
        updateElement(this.prevMonthButton, {
            events: {
                click: () => this.handlePrevMonth(),
            },
        });
        updateElement(this.nextMonthButton, {
            events: {
                click: () => this.handleNextMonth(),
            },
        });
        updateElement(this.monthPicker, {
            events: {
                change: () => this.handleMonthUpdate(),
            },
        });

        // Use event delegation on the calendar container instead of attaching
        // three separate listeners to each of the 42 day buttons. Stored as named
        // references so they can be properly removed in disconnect().
        this.boundCalendarKeydown = (e: Event) => {
            const ke = e as KeyboardEvent;
            this.handleArrowKeys(ke);
            const target = ke.target as HTMLElement;
            if (target?.classList.contains('ui-calendar__daybutton')) {
                this.handleKeyboardSelect(ke);
            }
        };
        this.boundDayClick = (e: Event) => {
            const target = e.target as HTMLElement;
            if (target?.classList.contains('ui-calendar__daybutton')) {
                this.handleDaySelect(e);
            }
        };
        // focusin bubbles (unlike focus), so one listener covers all day buttons.
        this.boundDayFocusin = (e: Event) => {
            const target = e.target as HTMLElement;
            if (target?.classList.contains('ui-calendar__daybutton')) {
                this.handleDayFocus(e);
            }
        };
        this.calendar.addEventListener('keydown', this.boundCalendarKeydown);
        this.calendar.addEventListener('click', this.boundDayClick);
        this.calendar.addEventListener('focusin', this.boundDayFocusin);

        this.observer = new MutationObserver(
            this.handleControlMutations.bind(this)
        );
        this.observer.observe(this.control, { attributes: true });
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        if (this.focusTimer !== undefined) {
            clearTimeout(this.focusTimer);
            delete this.focusTimer;
        }
        this.observer?.disconnect();
        if (this.boundCalendarKeydown) {
            this.calendar?.removeEventListener(
                'keydown',
                this.boundCalendarKeydown
            );
        }
        if (this.boundDayClick) {
            this.calendar?.removeEventListener('click', this.boundDayClick);
        }
        if (this.boundDayFocusin) {
            this.calendar?.removeEventListener('focusin', this.boundDayFocusin);
        }
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        // Only re-attach the handlers that were explicitly removed in disconnect().
        // hydrate() would duplicate listeners because updateElement() only adds.
        if (
            !this.boundCalendarKeydown ||
            !this.boundDayClick ||
            !this.boundDayFocusin
        ) {
            this.hydrate();
            return;
        }

        this.calendar.addEventListener('keydown', this.boundCalendarKeydown);
        this.calendar.addEventListener('click', this.boundDayClick);
        this.calendar.addEventListener('focusin', this.boundDayFocusin);

        this.observer ??= new MutationObserver(
            this.handleControlMutations.bind(this)
        );
        this.observer.observe(this.control, { attributes: true });
    }
}
